import { useState } from "react";
import { useNavigate } from "react-router-dom";
import CommonLayout from "../../../layouts/CommonLayout";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import {
  FaUserPlus,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaLock,
  FaUserShield,
  FaCheck,
  FaChevronDown,
  FaChevronUp,
  FaArrowLeft,
  FaCrown,
  FaKey,
} from "react-icons/fa";
import "./AdminManagement.css";

// Permission groups for organized display
const PERMISSION_GROUPS = {
  artist: {
    label: "Artist Management",
    icon: "🎤",
    permissions: ["artist:view", "artist:create", "artist:edit", "artist:delete"],
  },
  song: {
    label: "Song Management",
    icon: "🎵",
    permissions: ["song:view", "song:create", "song:edit", "song:delete"],
  },
  album: {
    label: "Album Management",
    icon: "💿",
    permissions: ["album:view", "album:create", "album:edit", "album:delete"],
  },
  playlist: {
    label: "Playlist Management",
    icon: "📋",
    permissions: ["playlist:view", "playlist:create", "playlist:edit", "playlist:delete"],
  },
  quiz: {
    label: "Quiz Management",
    icon: "❓",
    permissions: ["quiz:view", "quiz:create", "quiz:edit", "quiz:delete"],
  },
  genre: {
    label: "Genre Management",
    icon: "🏷️",
    permissions: ["genre:view", "genre:create", "genre:edit", "genre:delete"],
  },
  news: {
    label: "News Management",
    icon: "📰",
    permissions: ["news:view", "news:create", "news:edit", "news:delete"],
  },
  cache: {
    label: "Cache Management",
    icon: "⚡",
    permissions: ["cache:view", "cache:manage"],
  },
  contact: {
    label: "Contact Management",
    icon: "📞",
    permissions: ["contact:view", "contact:manage"],
  },
  subscribers: {
    label: "Subscribers Management",
    icon: "📧",
    permissions: ["subscribers:view", "subscribers:manage"],
  },
  youtube: {
    label: "YouTube Management",
    icon: "📺",
    permissions: ["youtube:view", "youtube:manage"],
  },
  podcast: {
    label: "Podcast Management",
    icon: "🎙️",
    permissions: ["podcast:view", "podcast:manage"],
  },
  songLyricsContributions: {
    label: "Song Lyrics Contributions",
    icon: "📝",
    permissions: ["song-lyrics-contributions:view", "song-lyrics-contributions:manage"],
  },
  albumContributions: {
    label: "Album Contributions",
    icon: "💿",
    permissions: ["album-contributions:view", "album-contributions:manage"],
  },
  songContributions: {
    label: "Song Contributions",
    icon: "🎵",
    permissions: ["song-contributions:view", "song-contributions:manage"],
  },
  artistBioContributions: {
    label: "Artist Bio Contributions",
    icon: "👤",
    permissions: ["artist-bio-contributions:view", "artist-bio-contributions:manage"],
  },
  videoContributions: {
    label: "Video Contributions",
    icon: "🎬",
    permissions: ["video-contributions:view", "video-contributions:manage"],
  },
  tags: {
    label: "Tags Management",
    icon: "🏷️",
    permissions: ["tags:view", "tags:manage"],
  },
  userActivity: {
    label: "User Activity",
    icon: "👁️",
    permissions: ["user-activity:view"],
  },
  user: {
    label: "User Management",
    icon: "👤",
    permissions: ["user:view", "user:edit", "user:delete"],
  },
  notification: {
    label: "Notification Management",
    icon: "🔔",
    permissions: ["notification:view", "notification:create", "notification:delete"],
  },
};

export default function AddAdmin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(["artist", "song"]);

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    mobile: "",
    password: "",
    confirmPassword: "",
    role: "admin",
    isActive: true,
    permissions: [],
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    // Clear error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const togglePermission = (permission) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission],
    }));
  };

  const toggleGroupAll = (groupKey) => {
    const groupPermissions = PERMISSION_GROUPS[groupKey].permissions;
    const allSelected = groupPermissions.every((p) => form.permissions.includes(p));

    setForm((prev) => ({
      ...prev,
      permissions: allSelected
        ? prev.permissions.filter((p) => !groupPermissions.includes(p))
        : [...new Set([...prev.permissions, ...groupPermissions])],
    }));
  };

  const toggleGroupExpand = (groupKey) => {
    setExpandedGroups((prev) =>
      prev.includes(groupKey)
        ? prev.filter((g) => g !== groupKey)
        : [...prev, groupKey]
    );
  };

  const validate = () => {
    const newErrors = {};

    if (!form.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!form.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    }

    if (!form.password) {
      newErrors.password = "Password is required";
    } else if (form.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      setLoading(true);

      const payload = {
        fullName: form.fullName.trim(),
        email: form.email.trim().toLowerCase(),
        mobile: form.mobile.trim(),
        password: form.password,
        role: form.role,
        isActive: form.isActive,
        permissions: form.role === "superadmin" ? [] : form.permissions,
      };

      const response = await allAPiServicesCall.createAdminUser(payload, getAuthConfig());

      if (response?.success) {
        navigate("/admin-management");
      }
    } catch (error) {
      console.error("Error creating admin:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatPermissionLabel = (permission) => {
    const [module, action] = permission.split(":");
    return `${action.charAt(0).toUpperCase() + action.slice(1)} ${module.replace("-", " ")}`;
  };

  const selectAllPermissions = () => {
    const allPermissions = Object.values(PERMISSION_GROUPS).flatMap(g => g.permissions);
    setForm(prev => ({ ...prev, permissions: allPermissions }));
  };

  const deselectAllPermissions = () => {
    setForm(prev => ({ ...prev, permissions: [] }));
  };

  return (
    <CommonLayout>
      <div className="admin-management-page container-fluid px-2 px-md-4">
        <div className="row justify-content-center">
          <div className="col-12 col-xl-10">
            {/* Enhanced Header */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
              <div>
                <button
                  className="btn btn-link p-0 mb-2 text-decoration-none d-flex align-items-center"
                  onClick={() => navigate("/admin-management")}
                  style={{ color: "var(--primary)" }}
                >
                  <FaArrowLeft className="me-2" size={14} />
                  <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>Back to Admin List</span>
                </button>
                <h2 className="mb-1">
                  <FaUserPlus className="me-2" />
                  Add New Admin
                </h2>
                <p className="text-muted mb-0">Create a new admin account with specific permissions</p>
              </div>

              {/* Role Selection Cards */}
              <div className="d-flex gap-2">
                <div
                  className={`role-selector-card ${form.role === "admin" ? "active" : ""}`}
                  onClick={() => setForm(prev => ({ ...prev, role: "admin" }))}
                >
                  <FaUserShield size={20} />
                  <span>Admin</span>
                </div>
                <div
                  className={`role-selector-card superadmin ${form.role === "superadmin" ? "active" : ""}`}
                  onClick={() => setForm(prev => ({ ...prev, role: "superadmin" }))}
                >
                  <FaCrown size={20} />
                  <span>Super Admin</span>
                </div>
              </div>
            </div>

            <form className="admin-form" onSubmit={handleSubmit}>
              {/* Basic Information Card */}
              <div className="admin-form-card mb-4">
                <div className="form-card-header">
                  <div className="header-icon">
                    <FaUser />
                  </div>
                  <div>
                    <h5>Basic Information</h5>
                    <p>Enter the admin's personal details</p>
                  </div>
                </div>
                <div className="form-card-body">
                  <div className="row g-4">
                    <div className="col-md-6">
                      <div className="form-group-enhanced">
                        <label className="form-label-enhanced">
                          <FaUser className="label-icon" />
                          Full Name <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control-enhanced ${errors.fullName ? "is-invalid" : ""}`}
                          name="fullName"
                          value={form.fullName}
                          onChange={handleChange}
                          placeholder="Enter full name"
                        />
                        {errors.fullName && <div className="error-message">{errors.fullName}</div>}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group-enhanced">
                        <label className="form-label-enhanced">
                          <FaEnvelope className="label-icon" />
                          Email Address <span className="required">*</span>
                        </label>
                        <input
                          type="email"
                          className={`form-control-enhanced ${errors.email ? "is-invalid" : ""}`}
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          placeholder="Enter email address"
                        />
                        {errors.email && <div className="error-message">{errors.email}</div>}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group-enhanced">
                        <label className="form-label-enhanced">
                          <FaPhone className="label-icon" />
                          Mobile Number <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control-enhanced ${errors.mobile ? "is-invalid" : ""}`}
                          name="mobile"
                          value={form.mobile}
                          onChange={handleChange}
                          placeholder="Enter mobile number"
                        />
                        {errors.mobile && <div className="error-message">{errors.mobile}</div>}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group-enhanced">
                        <label className="form-label-enhanced">
                          <FaUserShield className="label-icon" />
                          Account Status
                        </label>
                        <div className="status-toggle">
                          <input
                            type="checkbox"
                            id="isActive"
                            name="isActive"
                            checked={form.isActive}
                            onChange={handleChange}
                            className="status-checkbox"
                          />
                          <label htmlFor="isActive" className="status-label">
                            <span className={`status-text ${form.isActive ? "active" : "inactive"}`}>
                              {form.isActive ? "Active" : "Inactive"}
                            </span>
                            <small>Inactive accounts cannot access the admin panel</small>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Security Card */}
              <div className="admin-form-card mb-4">
                <div className="form-card-header security">
                  <div className="header-icon">
                    <FaKey />
                  </div>
                  <div>
                    <h5>Security Settings</h5>
                    <p>Set up the admin's login credentials</p>
                  </div>
                </div>
                <div className="form-card-body">
                  <div className="row g-4">
                    <div className="col-md-6">
                      <div className="form-group-enhanced">
                        <label className="form-label-enhanced">
                          <FaLock className="label-icon" />
                          Password <span className="required">*</span>
                        </label>
                        <input
                          type="password"
                          className={`form-control-enhanced ${errors.password ? "is-invalid" : ""}`}
                          name="password"
                          value={form.password}
                          onChange={handleChange}
                          placeholder="Min 6 characters"
                        />
                        {errors.password && <div className="error-message">{errors.password}</div>}
                        <div className="password-hint">
                          Use a strong password with letters, numbers & symbols
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group-enhanced">
                        <label className="form-label-enhanced">
                          <FaLock className="label-icon" />
                          Confirm Password <span className="required">*</span>
                        </label>
                        <input
                          type="password"
                          className={`form-control-enhanced ${errors.confirmPassword ? "is-invalid" : ""}`}
                          name="confirmPassword"
                          value={form.confirmPassword}
                          onChange={handleChange}
                          placeholder="Confirm password"
                        />
                        {errors.confirmPassword && <div className="error-message">{errors.confirmPassword}</div>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Permissions Section - Only show for admin role */}
              {form.role === "admin" && (
                <div className="admin-form-card mb-4">
                  <div className="form-card-header permissions">
                    <div className="header-icon">
                      <FaUserShield />
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                          <h5>Permissions</h5>
                          <p>Configure access levels for this admin</p>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                          <span className="permission-counter">
                            <strong>{form.permissions.length}</strong> selected
                          </span>
                          <div className="btn-group-sm">
                            <button
                              type="button"
                              className="btn-select-all"
                              onClick={selectAllPermissions}
                            >
                              Select All
                            </button>
                            <button
                              type="button"
                              className="btn-deselect-all"
                              onClick={deselectAllPermissions}
                            >
                              Clear All
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="form-card-body">
                    <div className="permissions-grid">
                      {Object.entries(PERMISSION_GROUPS).map(([key, group]) => (
                        <div key={key} className="permission-group">
                          <div
                            className="group-header"
                            onClick={() => toggleGroupExpand(key)}
                          >
                            <h5>
                              <span className="group-icon">{group.icon}</span>
                              {group.label}
                              <span className={`badge ${group.permissions.filter((p) => form.permissions.includes(p)).length === group.permissions.length ? "complete" : ""}`}>
                                {group.permissions.filter((p) => form.permissions.includes(p)).length}/{group.permissions.length}
                              </span>
                            </h5>
                            <div className="d-flex align-items-center gap-3">
                              <span
                                className="toggle-all"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleGroupAll(key);
                                }}
                              >
                                {group.permissions.every((p) => form.permissions.includes(p))
                                  ? "Deselect All"
                                  : "Select All"}
                              </span>
                              {expandedGroups.includes(key) ? <FaChevronUp /> : <FaChevronDown />}
                            </div>
                          </div>
                          {expandedGroups.includes(key) && (
                            <div className="group-body">
                              {group.permissions.map((permission) => (
                                <div
                                  key={permission}
                                  className={`permission-item ${form.permissions.includes(permission) ? "selected" : ""}`}
                                  onClick={() => togglePermission(permission)}
                                >
                                  <input
                                    type="checkbox"
                                    checked={form.permissions.includes(permission)}
                                    onChange={() => {}}
                                  />
                                  <label>{formatPermissionLabel(permission)}</label>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {form.role === "superadmin" && (
                <div className="superadmin-notice mb-4">
                  <div className="notice-icon">
                    <FaCrown />
                  </div>
                  <div className="notice-content">
                    <h5>Super Admin Role Selected</h5>
                    <p>
                      Super Admins have <strong>full access</strong> to all features and can manage other admin users.
                      No individual permission selection is needed.
                    </p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => navigate("/admin-management")}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <FaCheck className="me-2" />
                      Create Admin
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </CommonLayout>
  );
}
